import {
	ChannelType,
	MessageType,
	IMessage,
	IChat,
	IAttendance,
	IMessageContent,
	IChannel
} from "@/protocols/channel"
import { Client } from "@/protocols/clientCatalog"

import SocketService from "@/services/Socket"
import ErrorHandlerService from "@/services/ErrorHandler"
import { RequestPayload } from "@/protocols/socket"
import { ImportContactsHistoryType } from "@/pages/Admin/ClientCatalog/ImportContacts/ImportContactsHistory"
import { ErrorType } from "./useValidation"

type SendMessageInput = {
	channelType: ChannelType
	inboxChannelId: number
	inboxChannelChatId: number
	content: IMessageContent
	type: MessageType
	replyMessageId?: string
	mediaName?: string
	mediaUrl?: string
	mediaKey?: string
}

type SendMessageResponse = IMessage

type SetReadMessagesInput = {
	channelType: ChannelType
	inboxChannelChatId: number
	inboxChannelId: number
}

type TakeAttendanceInput = {
	channelType: ChannelType
	inboxChannelChatId: number
	inboxChannelId: number
}

type AssignAttendanceInput = {
	channelType: ChannelType
	inboxChannelChatId: number
	inboxChannelId: number
	assignedUserId?: number
	assignedTeamId?: number
	assignmentQueueType: "User" | "Team"
	assignmentObservation: string
	wasStartedBy: "attendant" | "customer" | "assign"
}

type FinishAttendanceInput = TakeAttendanceInput

type DeleteMessageInput = {
	channelType: ChannelType
	inboxChannelChatMessageId: string
	inboxChannelChatId: number
	inboxChannelId: number
}

type ScheduleChatMessagesLoadInput = {
	channelType: ChannelType
	inboxChannelChatId: number
	inboxChannelId: number
}

type ScheduleChatMessagesLoadResponse = {
	chatStatus: {
		areAllMessagesLoaded?: boolean
	}
}

type ScheduleChatMessagesLoadSyncInput = {
	channelType: ChannelType
	inboxChannelChatId: number
	inboxChannelId: number
	beforeDate?: number
	afterDate?: number
}

type ScheduleManyChatMessagesLoadSyncInput = {
	channelType: ChannelType
	inboxChannelId: number
	afterDate?: number
}

type ScheduleChatMessagesLoadSyncExtraData = RequestPayload & {
	extraData?: {
		beforeDate?: number
		afterDate?: number
	}
}

type SetChatUnreadInput = {
	channelType: ChannelType
	inboxChannelChatId: number
}

type StartChatInput = {
	channelType: ChannelType
	clientId: number
	contactId: number
	inboxChannelId: number
}

type SyncGroupsInput = {
	channelType: ChannelType
	inboxChannelId: number
}

type DeleteGroupInput = {
	channelType: ChannelType
	groupId: number
	inboxChannelId: number
}

type SetGroupInput = {
	channelType: ChannelType
	groupId: number
	name?: string
	inboxChannelId: number
}

type OnContactsImportStatusChangedType = ImportContactsHistoryType

type OnNewChatInput = (chat: IChat) => Promise<void> | void

type OnNewClientInput = (chat: Client) => Promise<void> | void

type OnChatAttendanceTaken = (attendance: IAttendance) => Promise<void> | void

type OnChatAttendanceAssigned = (attendance: IAttendance) => Promise<void> | void

type OnChatAttendanceFinished = (attendance: IAttendance) => Promise<void> | void

type OnNewChatAttendanceNotification = (message: IMessage) => Promise<void> | void

type OnNewMessage = (message: IMessage) => Promise<void> | void

type OnMessageStatusChanged = (message: IMessage) => Promise<void> | void

type OnMessageDeleted = (message: IMessage) => Promise<void> | void

type OnClientDataChanged = (client: Client) => Promise<void> | void

type OnChannelChanged = (channel: IChannel) => Promise<void> | void

type OnContactsImportStatusChanged = (importContacts: OnContactsImportStatusChangedType) => Promise<void> | void

type OnDisconnect = () => Promise<void> | void

type OnSocketReady = () => Promise<void> | void

const useSocket = () => {
	const sendMessage = async (input: SendMessageInput): Promise<SendMessageResponse | null> => {
		try {
			const messageSent = await SocketService.emit<IMessage, "">("SendMessage", input.channelType, {
				inboxChannel: {
					id: input.inboxChannelId
				},
				inboxChannelChat: {
					id: input.inboxChannelChatId
				},
				inboxChannelChatMessage: {
					content: input.content,
					type: input.type,
					replyMessageId: input.replyMessageId,
					mediaName: input.mediaName,
					mediaUrl: input.mediaUrl,
					mediaKey: input.mediaKey,
					feature: "attendance"
				}
			})

			return messageSent
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return null
		}
	}

	const finishAttendance = async (input: FinishAttendanceInput): Promise<boolean> => {
		try {
			await SocketService.emit("FinishAttendance", input.channelType, {
				inboxChannel: {
					id: input.inboxChannelId
				},
				inboxChannelChat: {
					id: input.inboxChannelChatId
				}
			})

			return true
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return false
		}
	}

	const setReadMessages = async (input: SetReadMessagesInput): Promise<void> => {
		try {
			await SocketService.emit("SetReadMessages", input.channelType, {
				inboxChannel: {
					id: input.inboxChannelId
				},
				inboxChannelChat: {
					id: input.inboxChannelChatId
				}
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const takeAttendance = async (input: TakeAttendanceInput): Promise<boolean> => {
		try {
			await SocketService.emit("TakeAttendance", input.channelType, {
				inboxChannel: {
					id: input.inboxChannelId
				},
				inboxChannelChat: {
					id: input.inboxChannelChatId
				},
				attendance: {
					wasStartedBy: "attendant"
				}
			})

			return true
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return false
		}
	}

	const assignAttendance = async (input: AssignAttendanceInput): Promise<boolean> => {
		try {
			await SocketService.emit("AssignAttendance", input.channelType, {
				inboxChannel: {
					id: input.inboxChannelId
				},
				inboxChannelChat: {
					id: input.inboxChannelChatId
				},
				attendance: {
					wasStartedBy: input.wasStartedBy,
					assignData: {
						assignedUserId: input.assignedUserId,
						assignedTeamId: input.assignedTeamId,
						assignmentQueueType: input.assignmentQueueType,
						assignmentObservation: input.assignmentObservation
					}
				}
			})

			return true
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return false
		}
	}

	const deleteMessage = async (input: DeleteMessageInput): Promise<void> => {
		try {
			await SocketService.emit("DeleteMessage", input.channelType, {
				inboxChannel: {
					id: input.inboxChannelId
				},
				inboxChannelChat: {
					id: input.inboxChannelChatId
				},
				inboxChannelChatMessage: {
					id: input.inboxChannelChatMessageId
				}
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const scheduleChatMessagesLoad = async (input: ScheduleChatMessagesLoadInput): Promise<ScheduleChatMessagesLoadResponse | null> => {
		try {
			const socketResponse = await SocketService.emit<ScheduleChatMessagesLoadResponse, "">("ScheduleChatMessagesLoad", input.channelType, {
				inboxChannel: {
					id: input.inboxChannelId
				},
				inboxChannelChat: {
					id: input.inboxChannelChatId
				}
			})

			return socketResponse
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)

			return null
		}
	}

	const scheduleChatMessagesSync = async (input: ScheduleChatMessagesLoadSyncInput): Promise<boolean> => {
		try {
			await SocketService.emit("ScheduleChatMessagesSync", input.channelType, {
				inboxChannel: {
					id: input.inboxChannelId
				},
				inboxChannelChat: {
					id: input.inboxChannelChatId
				},
				extraData: {
					afterDate: input.afterDate,
					beforeDate: input.beforeDate
				}
			} as ScheduleChatMessagesLoadSyncExtraData)

			return true
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)

			return false
		}
	}

	const scheduleManyChatMessagesSync = async (input: ScheduleManyChatMessagesLoadSyncInput): Promise<boolean> => {
		try {
			await SocketService.emit("ScheduleManyChatMessagesSync", input.channelType, {
				inboxChannel: {
					id: input.inboxChannelId
				},
				extraData: {
					afterDate: input.afterDate
				}
			} as ScheduleChatMessagesLoadSyncExtraData)

			return true
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)

			return false
		}
	}

	const setChatUnread = async (input: SetChatUnreadInput): Promise<void> => {
		try {
			await SocketService.emit("SetChatUnread", input.channelType, {
				inboxChannelChat: {
					id: input.inboxChannelChatId
				}
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const startChat = async (input: StartChatInput): Promise<IChat | null> => {
		try {
			const { detailedChat } = await SocketService.emit<{ detailedChat: IChat }, "">("StartChat", input.channelType, {
				client: {
					id: input.clientId
				},
				contact: {
					id: input.contactId
				},
				inboxChannel: {
					id: input.inboxChannelId
				}
			})

			return detailedChat
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return null
		}
	}

	const syncGroups = async (input: SyncGroupsInput): Promise<boolean> => {
		try {
			await SocketService.emit("SyncGroups", input.channelType, {
				inboxChannel: {
					id: input.inboxChannelId
				}
			})

			return true
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return false
		}
	}

	const deleteGroup = async (input: DeleteGroupInput): Promise<boolean> => {
		try {
			await SocketService.emit("DeleteGroup", input.channelType, {
				inboxChannel: {
					id: input.inboxChannelId
				},
				group: {
					id: input.groupId
				}
			})

			return true
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return false
		}
	}

	const setGroup = async (input: SetGroupInput): Promise<void> => {
		/**
		 * Do no write this method inside a try/catch since we have important
		 * validation messages when it throws an error.
		 */
		await SocketService.emit("SetGroup", input.channelType, {
			inboxChannel: {
				id: input.inboxChannelId
			},
			group: {
				id: input.groupId,
				name: input.name
			}
		})
	}

	const onNewChat = (fn: OnNewChatInput): void => {
		SocketService.on<IChat, "">("NewChat", fn)
	}

	const onNewAssignedChat = (fn: OnNewChatInput): void => {
		SocketService.on<IChat, "">("NewAssignedChat", fn)
	}

	const onNewClient = (fn: OnNewClientInput): void => {
		SocketService.on<Client, "">("NewClient", fn)
	}

	const onChatAttendanceTaken = (fn: OnChatAttendanceTaken): void => {
		SocketService.on<IAttendance, "">("ChatAttendanceTaken", fn)
	}

	const onChatAttendanceAssigned = (fn: OnChatAttendanceAssigned): void => {
		SocketService.on<IAttendance, "">("ChatAttendanceAssigned", fn)
	}

	const onChatAttendanceFinished = (fn: OnChatAttendanceFinished): void => {
		SocketService.on<IAttendance, "">("ChatAttendanceFinished", fn)
	}

	const onNewChatAttendanceNotification = (fn: OnNewChatAttendanceNotification): void => {
		SocketService.on<IMessage, "">("NewChatAttendanceNotification", fn)
	}

	const onMessageStatusChanged = (fn: OnMessageStatusChanged): void => {
		SocketService.on<IMessage, "">("MessageStatusChanged", fn)
	}

	const onMessageDeleted = (fn: OnMessageDeleted): void => {
		SocketService.on<IMessage, "">("MessageDeleted", fn)
	}

	const onClientDataChanged = (fn: OnClientDataChanged): void => {
		SocketService.on<Client, "">("ClientDataChanged", fn)
	}

	const onNewMessage = (fn: OnNewMessage): void => {
		SocketService.on<IMessage, "">("NewMessage", fn)
	}

	const onChannelChanged = (fn: OnChannelChanged): void => {
		SocketService.on<IChannel, "">("ChannelChanged", fn)
	}

	const onContactsImportStatusChanged = (fn: OnContactsImportStatusChanged): void => {
		SocketService.on<OnContactsImportStatusChangedType, "">("ContactsImportStatusChanged", fn)
	}

	const onDisconnect = (fn: OnDisconnect): void => {
		SocketService.on("disconnect", fn)
	}

	const onSocketReady = (fn: OnSocketReady): void => {
		SocketService.on("SocketReady", fn)
	}

	return {
		finishAttendance,
		sendMessage,
		setReadMessages,
		takeAttendance,
		assignAttendance,
		deleteMessage,
		scheduleChatMessagesLoad,
		scheduleChatMessagesSync,
		scheduleManyChatMessagesSync,
		setChatUnread,
		startChat,
		syncGroups,
		deleteGroup,
		setGroup,
		onChatAttendanceTaken,
		onChatAttendanceAssigned,
		onNewChat,
		onNewAssignedChat,
		onNewClient,
		onNewChatAttendanceNotification,
		onNewMessage,
		onChannelChanged,
		onDisconnect,
		onSocketReady,
		onMessageStatusChanged,
		onMessageDeleted,
		onClientDataChanged,
		onChatAttendanceFinished,
		onContactsImportStatusChanged
	}
}

export default useSocket
